import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "@/lib/apiClient";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CLUSTERS } from "@/lib/constants";
import { CheckCircle2, Loader2 } from "lucide-react";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import { PrivacyConsent, LegalFooterLinks } from "@/components/LegalConsent";

export default function PublicRequest() {
  const { codice } = useParams();
  const [partner, setPartner] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ ragione_sociale: "", cluster: "", email: "", telefono: "", referente: "", note: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [privacyOk, setPrivacyOk] = useState(false);

  useEffect(() => {
    api.get(`/public/partner/${codice}`).then(({ data }) => setPartner(data)).catch(() => setNotFound(true));
  }, [codice]);

  const submit = async (e) => {
    e.preventDefault();
    if (!privacyOk) { setError("Per procedere devi accettare l'informativa privacy."); return; }
    setLoading(true);
    setError("");
    try {
      await api.post(`/public/requests/${codice}`, form);
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (notFound)
    return (
      <div className="flex min-h-screen items-center justify-center bg-bone p-6 text-center">
        <p className="text-muted-foreground">Link non valido o partner non attivo.</p>
      </div>
    );

  return (
    <div className="min-h-screen bg-bone">
      <div className="mx-auto flex max-w-lg flex-col px-5 py-10">
        <Brand />
        {sent ? (
          <div data-testid="public-request-success" className="mt-16 rounded-2xl border border-border bg-white p-8 text-center shadow-sm">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
            <h2 className="mt-4 font-heading text-2xl font-semibold text-forest">Richiesta inviata!</h2>
            <p className="mt-2 text-muted-foreground">Grazie. {partner?.ragione_sociale} ti ricontatterà a breve.</p>
          </div>
        ) : (
          <div className="mt-10 rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-8">
            <h1 className="font-heading text-2xl font-semibold text-forest">Richiedi un preventivo</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              RC Economia Solidale — tramite {partner?.ragione_sociale || codice}. Pochi campi, ti pensiamo noi.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label>Nome dell'ente / organizzazione</Label>
                <Input data-testid="public-ragione-sociale" required value={form.ragione_sociale}
                  onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} className="h-12" />
              </div>
              <div className="space-y-1.5">
                <Label>Tipologia</Label>
                <Select value={form.cluster} onValueChange={(v) => setForm({ ...form, cluster: v })}>
                  <SelectTrigger data-testid="public-cluster" className="h-12"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>{CLUSTERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Referente</Label>
                  <Input data-testid="public-referente" value={form.referente} onChange={(e) => setForm({ ...form, referente: e.target.value })} className="h-12" />
                </div>
                <div className="space-y-1.5">
                  <Label>Telefono</Label>
                  <Input data-testid="public-telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="h-12" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input data-testid="public-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12" />
              </div>
              <div className="space-y-1.5">
                <Label>Note</Label>
                <Textarea data-testid="public-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <PrivacyConsent checked={privacyOk} onCheckedChange={setPrivacyOk} testid="public-privacy-consent" />
              <Button type="submit" data-testid="public-submit" disabled={loading || !privacyOk}
                className="h-12 w-full rounded-xl bg-terracotta text-base hover:bg-terracotta-hover">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Invia richiesta"}
              </Button>
            </form>
          </div>
        )}
        <div className="mt-6"><ComplianceNotice /></div>
        <LegalFooterLinks className="mt-4" />
        <p className="mt-4 text-center text-xs text-muted-foreground">Portale Partner Assietica · Terzo Settore</p>
      </div>
    </div>
  );
}
